import { WithId } from "../../hooks/firestore-hooks";
import { CommentDocument } from "../../lib/firestore-schema";
import { CommentWithId } from "../../models/document/document-comments-manager";
import { DocumentContentModelType } from "../../models/document/document-content";

export interface ChatCommentThread {
  title: string | null | undefined;
  tileId: string | null;
  tileType: string | null;
  comments: WithId<CommentDocument>[];
  isDeletedTile: boolean;
}

// Sorts comments into document order: document-level comments first, then tile comments
// in the order their tiles appear in the document. Tiles not found in the ordering
// (e.g. deleted tiles) sort after all known tiles.
export function sortCommentsInDocumentOrder(
  comments: CommentWithId[],
  content: DocumentContentModelType
): CommentWithId[] {
  const ordering = content.getTilesInDocumentOrder() || [];
  const docComments = comments.filter(c => c.tileId == null);
  const afterAll = ordering.length;
  const tileComments = comments.filter(c => c.tileId != null)
    .sort((a, b) => {
      const aIdx = ordering.indexOf(a.tileId!);
      const bIdx = ordering.indexOf(b.tileId!);
      return (aIdx < 0 ? afterAll : aIdx) - (bIdx < 0 ? afterAll : bIdx);
    });
  return [...docComments, ...tileComments];
}

// Expects a list of comments in Document order with document level comments (with no (null) tileId) first.
export function makeChatThreads (
    commentsInDocumentOrder: WithId<CommentDocument>[] | undefined,
    content: DocumentContentModelType | undefined,
    docTitle?: string) : ChatCommentThread[] {
  const chatThreads: ChatCommentThread[] = [];
  // Since the comments are sorted by tile id already, each time we encounter a new tile id, we make a new thread.
  commentsInDocumentOrder?.forEach((comment, idx)=> {
    // If the comment's tile id is the same as the most recent one, add it there
    const tileId = comment.tileId === undefined ? null : comment.tileId;
    if (chatThreads.length > 0 && tileId === chatThreads[chatThreads.length - 1].tileId) {
      chatThreads[chatThreads.length - 1].comments.push(comment);
    } else {
      // Start a new thread.
      let isTileComment = false;
      let tile;
      if (comment.tileId != null) {
        isTileComment = true;
        tile = content?.getTile(comment.tileId);
      }
      const isDeletedTile = isTileComment && !tile;
      chatThreads.push({
        title: (isTileComment ? (isDeletedTile ? "Deleted Tile" : tile?.computedTitle) : docTitle) ?? null,
        tileType: isTileComment && tile ? tile.content?.type : null,
        tileId: isTileComment ? tileId : null,
        comments: [comment],
        isDeletedTile,
      });
    }
  });
  return chatThreads;
}
